import React, { useState, useMemo } from "react";
import {
  GradeStateBtn,
  SmallPlainInsetBtnIcon,
  GlassBtn,
} from "../../components/buttons/index";
import {
  LeftArrowIcon,
  RightArrowIcon,
  TrashCanIcon,
  MoneyBagIcon,
} from "../../components/icons/index";
import { apiDropCurrentUserBrandAmbassadorShift } from "../../api/backend/brandAmbassador";
import {TIME_ZONES} from "../../constants/time"
import "./workCalendar.scss";

function getDaysInMonth(year, monthIndex) {
  const date = new Date(year, monthIndex, 1);
  const days = [];
  while (date.getMonth() === monthIndex) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
}

export default function WorkCalendar({
  role,
  schedules = [],
  ambassadorRecords = [],
  onDropShift,
  setGradingEvent,
  setPayEvent,
  currentUserName,
}) {
  // — calendar state
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);

  // — build lookups
  const scheduleAmbassadorLookup = useMemo(() => {
    return ambassadorRecords.reduce((acc, amb) => {
      acc[amb.teamWorkScheduleId] = acc[amb.teamWorkScheduleId] || [];
      acc[amb.teamWorkScheduleId].push(amb);
      return acc;
    }, {});
  }, [ambassadorRecords]);

  const workDaysLookup = useMemo(() => {
    const lookup = {};
    schedules.forEach((evt) => {
      const d = evt.date instanceof Date ? evt.date : new Date(evt.date);
      const key = d.toISOString().slice(0, 10);
      lookup[key] = lookup[key] || [];
      lookup[key].push(evt);
    });
    return lookup;
  }, [schedules]);
  // Details panel helper: map zone to label
  function getZoneLabel(zoneValue) {
    const entry = TIME_ZONES.find((z) => z.value === zoneValue);
    return entry ? entry.label : zoneValue;
  }

  const daysInMonth = useMemo(
    () => getDaysInMonth(year, monthIndex),
    [year, monthIndex]
  );
  const firstDayOfWeekIndex = daysInMonth[0].getDay();
  const monthLabel = new Date(year, monthIndex).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  function formatDate(d) {
    return d.toISOString().split("T")[0];
  }

  // — handlers
  const handlePrevMonth = () => {
    let nm = monthIndex - 1;
    let ny = year;
    if (nm < 0) {
      nm = 11;
      ny -= 1;
    }
    if (
      ny < today.getFullYear() ||
      (ny === today.getFullYear() && nm < today.getMonth())
    ) {
      return;
    }
    setMonthIndex(nm);
    setYear(ny);
    setSelectedDay(null);
  };

  const handleNextMonth = () => {
    let nm = monthIndex + 1;
    let ny = year;
    if (nm > 11) {
      nm = 0;
      ny += 1;
    }
    setMonthIndex(nm);
    setYear(ny);
    setSelectedDay(null);
  };

  async function handleDropShift(eventId) {
    try {
      await apiDropCurrentUserBrandAmbassadorShift(eventId);
      onDropShift?.(eventId);
    } catch (err) {
      console.error("Failed to drop shift:", err.response || err);
      if (err.response?.status === 403) {
        alert("You’re not authorized to drop this shift.");
      } else {
        alert("Could not drop shift—please try again.");
      }
    }
  }

  const isPrevDisabled =
    year === today.getFullYear() && monthIndex === today.getMonth();

  // — derive today’s selected-day events
  const selectedDayStr = selectedDay ? formatDate(selectedDay) : null;
  const selectedDayEvents = selectedDayStr
    ? workDaysLookup[selectedDayStr] || []
    : [];

  return (
    <div className="work-calendar-container">
      {/* Nav */}
      <div className="calendar-nav">
        {!isPrevDisabled && (
          <GlassBtn
            glassBtnImage={
              <LeftArrowIcon height="15px" width="15px" fillColor="#fdfd96" />
            }
            glassBtnHeight="25px"
            glassBtnWidth="100px"
            glassBtnTextFontSize="1.2rem"
            glassBtnFontWeight="900"
            glassBtnText="Prev"
            disabled={isPrevDisabled}
            glassBtnFontColor="#fdfd96"
            glassBtnTextDecorationColor="#fdfd96"
            onClick={handlePrevMonth}
            hi2Style={{ transform: "rotate(90deg) translate(-70px, 50px)" }}
            hi3Style={{ transform: "rotate(90deg) translate(-70px, -50px)" }}
            glassHoverStyle={{ height: "55px" }}
          />
        )}
        <h2 className="calendar-title">{monthLabel}</h2>
        <GlassBtn
          glassBtnImage={
            <RightArrowIcon height="15px" width="15px" fillColor="#fdfd96" />
          }
          glassBtnHeight="25px"
          glassBtnWidth="100px"
          glassBtnTextFontSize="1.2rem"
          glassBtnFontWeight="900"
          glassBtnText="Next"
          glassBtnFontColor="#fdfd96"
          glassBtnTextDecorationColor="#fdfd96"
          onClick={handleNextMonth}
          hi2Style={{ transform: "rotate(90deg) translate(-80px, 50px)" }}
          hi3Style={{ transform: "rotate(90deg) translate(-80px, -50px)" }}
          glassHoverStyle={{ height: "55px" }}
        />
      </div>

      {/* Days of week */}
      <div className="day-names-row">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="day-name">
            {d}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="calendar-grid">
        {Array(firstDayOfWeekIndex)
          .fill(null)
          .map((_, i) => (
            <div key={`b${i}`} className="calendar-cell blank" />
          ))}
        {daysInMonth.map((dateObj) => {
          const ds = formatDate(dateObj);
          const isSelected = ds === selectedDayStr;
          return (
            <div
              key={ds}
              className={`calendar-cell day-cell${isSelected ? " selected" : ""}`}
              onClick={() => setSelectedDay(dateObj)}
            >
              <span className="day-number">{dateObj.getDate()}</span>
              {workDaysLookup[ds]?.length > 0 && <div className="event-dot" />}
            </div>
          );
        })}
      </div>

      {/* Details panel */}
      {selectedDay && (
        <div className="day-details">
          <h3>
            {selectedDay.toLocaleDateString(undefined, {
              weekday: "long",
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </h3>

          {selectedDayEvents.length > 0 ? (
            <div className="shifts-list">
              {selectedDayEvents.map((evt) => {
                const start = evt.startTime; // Date object, used below for drop logic
                const end = evt.endTime;
                // (1) Your existing drop‐window calc:
                const nowMs = Date.now();
                const minsUntilStart = (start.getTime() - nowMs) / 60000;
                const disableDrop = minsUntilStart < 45;
                // (2) Then compute human‐readable strings for the UI:
                const startStr = start.toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                });
                const endStr = end.toLocaleTimeString([], {
                  hour: "numeric",
                  minute: "2-digit",
                  hour12: true,
                });
                const tzLabel = getZoneLabel(evt.timeZone);
                const participants = scheduleAmbassadorLookup[evt.id] || [];
                const bas = participants.filter(
                  (p) => p.rolePerformed === "brandAmbassador"
                );
                const pendingBas = bas.filter((p) => !p.standingGraded);
                const fls = participants.filter(
                  (p) => p.rolePerformed === "fieldLeader"
                );

                let users;
                if (role === "brandAmbassador") {
                  users = bas.filter((p) => p.userName === currentUserName);
                } else if (role === "fieldLeader") {
                  users = [
                    { userName: currentUserName, rolePerformed: "fieldLeader" },
                    ...bas,
                  ];
                } else {
                  users = [
                    { userName: currentUserName, rolePerformed: "admin" },
                    ...fls.filter((f) => f.userName !== currentUserName),
                    ...bas,
                  ];
                }

                const names = [...new Set(users.map((u) => u.userName))].join(
                  ", "
                );
                const positions = [
                  ...new Set(users.map((u) => u.rolePerformed)),
                ].join(", ");
                const hoursSinceEnd = (Date.now() - end.getTime()) / 36e5;

                // find this user’s record
                const myRecord = participants.find(
                  (p) =>
                    p.userName === currentUserName &&
                    p.rolePerformed === "brandAmbassador"
                );

                return (
                  <div key={evt.id} className="shift-card">
                    <p>
                      <strong>Instructions:</strong> {evt.instructions}
                    </p>
                    <p>
                      <strong>Location:</strong> {evt.location}
                    </p>
                    <p>
                      <strong>Time Slot:</strong> {startStr} – {endStr}{" "}
                      <em>({tzLabel})</em>
                    </p>
                    <p>
                      <strong>User Name:</strong> {names}
                    </p>
                    <p>
                      <strong>Position:</strong> {positions}
                    </p>
                    <div className="actions">
                      {role === "brandAmbassador" && myRecord && (
                        <SmallPlainInsetBtnIcon
                          onClick={() => handleDropShift(myRecord.eventId)}
                          disabled={disableDrop}
                          text="Drop"
                          icon={TrashCanIcon}
                          width="80px"
                        />
                      )}
                      {role === "fieldLeader" && pendingBas.length > 0 && (
                        <GradeStateBtn
                          evt={evt}
                          end={end}
                          hoursSinceEnd={hoursSinceEnd}
                          setGradingEvent={() =>
                            setGradingEvent({
                              scheduleId: evt.id,
                              pendingBAs: pendingBas.map((b) => ({
                                id: b.eventId,
                                userName: b.userName,
                              })),
                            })
                          }
                        />
                      )}
                      {role === "admin" && (
                        <div className="admin-schedule-action-btns">
                          {" "}
                          <SmallPlainInsetBtnIcon
                            onClick={() =>
                              myRecord && handleDropShift(myRecord.eventId)
                            }
                            disabled={disableDrop}
                            text="Drop"
                            icon={TrashCanIcon}
                            width="80px"
                            style={{ marginRight: 8 }}
                          />
                          {pendingBas.length > 0 && (
                            <GradeStateBtn
                              evt={evt}
                              end={end}
                              hoursSinceEnd={hoursSinceEnd}
                              setGradingEvent={() =>
                                setGradingEvent({
                                  scheduleId: evt.id,
                                  pendingBAs: pendingBas.map((b) => ({
                                    id: b.eventId,
                                    userName: b.userName,
                                  })),
                                })
                              }
                            />
                          )}
                          {/* Pay contractors button */}
                          <SmallPlainInsetBtnIcon
                            onClick={() =>
                              setPayEvent({
                                scheduleId: evt.id,
                                pendingPayees: participants.map((p) => ({
                                  id: p.eventId,
                                  userName: p.userName,
                                  rolePerformed: p.rolePerformed,
                                })),
                              })
                            }
                            text="Pay"
                            icon={MoneyBagIcon}
                            width="80px"
                            style={{ marginLeft: 8 }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p>No work events scheduled for this day.</p>
          )}
        </div>
      )}
    </div>
  );
}
